import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, ShieldCheck } from "lucide-react";
import {
  acceptAll,
  rejectAll,
  setConsent,
  getConsent,
} from "../lib/cookieConsent";
import { useCookieConsent } from "../lib/useCookieConsent";

/**
 * Iter-185.L — CarSavr Cookie Consent Banner.
 *
 * Sticky bottom-of-viewport banner that:
 *   • Appears on first visit (and any time the user hits "Cookie
 *     settings" in the footer to re-prompt).
 *   • Defaults all non-essential categories to OFF.
 *   • Offers 3 actions: Reject all · Customize · Accept all.
 *   • Customize swaps the banner for a category-toggle panel.
 *
 * Design follows the Mercury / Stripe pattern: small footprint, no
 * dark-pattern colour weighting (all 3 buttons readable, Accept is
 * the primary CTA per the user's explicit acceptance flow, not a
 * neon trick to coerce a click).
 */
export default function CookieConsentBanner() {
  const { decided } = useCookieConsent();
  const [showBanner, setShowBanner] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [draft, setDraft] = useState({ analytics: false, marketing: false });

  // Mount-time reveal — wait 1s after first paint so we don't compete
  // with hero LCP and don't flash on instant navigation.
  useEffect(() => {
    if (decided) {
      setShowBanner(false);
      return undefined;
    }
    const t = setTimeout(() => setShowBanner(true), 800);
    // Seed customize draft from current state.
    const current = getConsent();
    setDraft({ analytics: current.analytics, marketing: current.marketing });
    return () => clearTimeout(t);
  }, [decided]);

  if (!showBanner) return null;

  const handleAcceptAll = () => {
    acceptAll();
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    rejectAll();
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    setConsent({ analytics: draft.analytics, marketing: draft.marketing });
    setShowBanner(false);
  };

  return (
    <div
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-live="polite"
      // Iter-275.AW (Phase 4 #7) — Mobile: bottom card (max 320 px
      // wide) so the hero "Compare Insurance" CTA stays visible AND
      // clickable on first paint at <400 px widths.
      // Iter-278.D — Mobile horizontal centring (founder audit:
      // "Mobile cookies needs to be centered — looks off"). We keep
      // the 320 px cap but center it via flex+justify-center instead
      // of pinning to left-3 so the card sits flush with the
      // viewport's horizontal centre.
      // Desktop (sm+) keeps the centred max-w-3xl footprint.
      className="fixed inset-x-0 bottom-3 z-[110] px-3 pointer-events-none flex justify-center sm:bottom-0 sm:px-4 sm:pb-4"
    >
      <div className="pointer-events-auto w-full max-w-[320px] sm:max-w-3xl bg-white border border-slate-200 shadow-2xl">
        {!customizing ? (
          <CompactBanner
            onAcceptAll={handleAcceptAll}
            onRejectAll={handleRejectAll}
            onCustomize={() => setCustomizing(true)}
          />
        ) : (
          <CustomizePanel
            draft={draft}
            onChange={setDraft}
            onSave={handleSavePreferences}
            onAcceptAll={handleAcceptAll}
            onRejectAll={handleRejectAll}
            onClose={() => setCustomizing(false)}
          />
        )}
      </div>
    </div>
  );
}

function CompactBanner({ onAcceptAll, onRejectAll, onCustomize }) {
  return (
    <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 p-3 md:p-5">
      <div className="flex items-start gap-3 md:flex-1 min-w-0">
        <span
          aria-hidden
          className="hidden md:inline-flex shrink-0 w-9 h-9 items-center justify-center bg-emerald-50 border border-emerald-200"
        >
          <Cookie className="w-4 h-4 text-[#10B981]" strokeWidth={2.5} />
        </span>
        <div className="min-w-0">
          <p
            id="cookie-consent-title"
            className="font-display font-black text-sm md:text-base text-slate-950 leading-tight"
          >
            We use cookies to improve CarSavr.
          </p>
          {/* Iter-196.X (F-CR-01 / F-UX-13 from audit) — Mobile-compact
              variant. The original 50-word body paragraph ate ~40% of
              the mobile first-fold on every page load until consent
              was given (single largest CRO blocker per the iter-196.W
              audit). The full body copy is now visible only on md+;
              mobile gets a single-line micro-explanation + a link to
              the privacy policy via the Customize panel. The reject-
              all action remains accessible on mobile inside the
              Customize panel (data-testid="cookie-consent-customize-
              reject-all"), so the consent affordance set is identical
              — just visually leaner. */}
          <p
            data-testid="cookie-consent-body-mobile"
            className="text-[11px] text-slate-600 mt-0.5 leading-snug md:hidden"
          >
            We measure what saves drivers money. Off until you say yes.{" "}
            <Link
              to="/privacy"
              data-testid="cookie-consent-privacy-link-mobile"
              className="underline underline-offset-2 hover:text-slate-950"
            >
              Privacy
            </Link>
          </p>
          <p className="hidden md:block text-[12px] text-slate-600 mt-1 leading-relaxed">
            Essential cookies always run so login + saved comparisons
            work. Analytics cookies help us see which guides + calculators
            actually save drivers money — they&apos;re off until you say
            yes. Read our{" "}
            <Link
              to="/privacy"
              data-testid="cookie-consent-privacy-link"
              className="underline underline-offset-2 hover:text-slate-950"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>
      <div
        className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 md:flex-row md:shrink-0"
        data-testid="cookie-consent-actions"
      >
        <button
          type="button"
          onClick={onRejectAll}
          data-testid="cookie-consent-reject-all"
          className="hidden md:inline-flex items-center justify-center px-4 py-2.5 text-xs font-bold uppercase tracking-[0.14em] border border-slate-300 hover:border-slate-950 text-slate-700 hover:text-slate-950 transition-colors min-h-[44px]"
        >
          Reject all
        </button>
        <button
          type="button"
          onClick={onCustomize}
          data-testid="cookie-consent-customize"
          className="px-3 md:px-4 py-2 md:py-2.5 text-xs font-bold uppercase tracking-[0.14em] border border-slate-300 hover:border-slate-950 text-slate-700 hover:text-slate-950 transition-colors min-h-[40px] md:min-h-[44px]"
        >
          {/* Mobile gets a shorter label so the 2-button row fits a
              single line on iPhone SE widths. */}
          <span className="md:hidden">Settings</span>
          <span className="hidden md:inline">Customize</span>
        </button>
        <button
          type="button"
          onClick={onAcceptAll}
          data-testid="cookie-consent-accept-all"
          className="px-3 md:px-4 py-2 md:py-2.5 text-xs font-bold uppercase tracking-[0.14em] bg-[#10B981] hover:bg-[#34D399] text-white transition-colors min-h-[40px] md:min-h-[44px]"
        >
          Accept all
        </button>
      </div>
    </div>
  );
}

function CustomizePanel({ draft, onChange, onSave, onAcceptAll, onRejectAll, onClose }) {
  return (
    <div data-testid="cookie-consent-customize-panel">
      <header className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-slate-200">
        <div className="flex items-center gap-2.5">
          <ShieldCheck className="w-4 h-4 text-[#10B981]" strokeWidth={2.5} />
          <p className="font-display font-black text-sm text-slate-950">
            Cookie preferences
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="cookie-consent-customize-close"
          aria-label="Close cookie preferences"
          className="w-9 h-9 inline-flex items-center justify-center hover:bg-slate-100"
        >
          <X className="w-4 h-4 text-slate-700" />
        </button>
      </header>
      <div className="px-4 md:px-5 py-4 space-y-4">
        <CategoryToggle
          id="essential"
          label="Essential"
          checked
          locked
          description="Required for login, saved comparisons, CSRF protection, and basic site preferences. Can't be turned off — without these the site doesn't work."
        />
        <CategoryToggle
          id="analytics"
          label="Analytics"
          checked={draft.analytics}
          onChange={(v) => onChange({ ...draft, analytics: v })}
          description="Anonymized usage data (PostHog + Google Analytics 4). Tells us which guides + calculators actually help drivers save money. No personal info ever sold or shared with advertisers."
        />
        <CategoryToggle
          id="marketing"
          label="Marketing"
          checked={draft.marketing}
          onChange={(v) => onChange({ ...draft, marketing: v })}
          description="Reserved for remarketing / conversion pixels. We currently DO NOT run any marketing trackers — this toggle is here so we don't have to re-prompt every visitor if that ever changes."
        />
      </div>
      <footer className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 px-4 md:px-5 py-3 border-t border-slate-200 bg-slate-50">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onRejectAll}
            data-testid="cookie-consent-customize-reject-all"
            className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] border border-slate-300 hover:border-slate-950 text-slate-700 hover:text-slate-950 transition-colors min-h-[40px]"
          >
            Reject all
          </button>
          <button
            type="button"
            onClick={onAcceptAll}
            data-testid="cookie-consent-customize-accept-all"
            className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] border border-slate-300 hover:border-slate-950 text-slate-700 hover:text-slate-950 transition-colors min-h-[40px]"
          >
            Accept all
          </button>
        </div>
        <button
          type="button"
          onClick={onSave}
          data-testid="cookie-consent-save-preferences"
          className="px-4 py-2.5 text-xs font-bold uppercase tracking-[0.14em] bg-[#0A2540] hover:bg-slate-900 text-white transition-colors min-h-[44px]"
        >
          Save preferences
        </button>
      </footer>
    </div>
  );
}

function CategoryToggle({ id, label, description, checked, locked, onChange }) {
  return (
    <label
      htmlFor={`cookie-cat-${id}`}
      data-testid={`cookie-consent-cat-${id}`}
      className={`flex items-start gap-3 border border-slate-200 p-3 ${
        locked ? "bg-slate-50" : "hover:border-slate-300"
      }`}
    >
      <span className="relative inline-flex items-center cursor-pointer mt-0.5">
        <input
          id={`cookie-cat-${id}`}
          type="checkbox"
          checked={Boolean(checked)}
          disabled={Boolean(locked)}
          onChange={(e) => onChange && onChange(e.target.checked)}
          className="sr-only peer"
          data-testid={`cookie-consent-cat-${id}-input`}
        />
        <span
          aria-hidden
          className={`block w-9 h-5 rounded-full transition-colors ${
            checked ? "bg-[#10B981]" : "bg-slate-300"
          } ${locked ? "opacity-70" : "peer-focus:ring-2 peer-focus:ring-[#10B981]/40"}`}
        />
        <span
          aria-hidden
          className={`absolute top-0.5 left-0.5 block w-4 h-4 bg-white rounded-full transition-transform ${
            checked ? "translate-x-4" : "translate-x-0"
          }`}
        />
      </span>
      <div className="min-w-0">
        <p className="text-sm font-bold text-slate-950 leading-tight">
          {label}
          {locked && (
            <span className="ml-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
              · Always on
            </span>
          )}
        </p>
        <p className="text-[12px] text-slate-600 mt-1 leading-relaxed">
          {description}
        </p>
      </div>
    </label>
  );
}
